import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface PivotalStep {
  id?: string;
  title: string;
  description?: string;
  target_date?: string;
  is_completed?: boolean;
  timeline_event_id?: string;
  sort_order?: number;
}

interface PivotalStepDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (step: PivotalStep) => void;
  initialData?: PivotalStep;
  eventId?: string;
  existingSteps?: PivotalStep[];
}

export default function PivotalStepDialog({
  open,
  onClose,
  onSave,
  initialData,
  eventId,
  existingSteps = []
}: PivotalStepDialogProps) {
  const [formData, setFormData] = useState<PivotalStep>({
    title: '',
    description: '',
    target_date: '',
    is_completed: false,
    timeline_event_id: eventId,
    sort_order: existingSteps.length
  });
  const [insertPosition, setInsertPosition] = useState<string>('end');

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      setInsertPosition('existing');
    } else {
      setFormData({
        title: '',
        description: '',
        target_date: '',
        is_completed: false,
        timeline_event_id: eventId,
        sort_order: existingSteps.length
      });
      setInsertPosition('end');
    }
  }, [initialData, eventId, open, existingSteps.length]);

  const handleSubmit = () => {
    if (formData.title) {
      let finalSortOrder = formData.sort_order || existingSteps.length;
      
      if (!initialData && insertPosition !== 'end') {
        const insertAfterIndex = parseInt(insertPosition);
        finalSortOrder = insertAfterIndex + 0.5; // Insert between steps
      }
      
      onSave({ ...formData, timeline_event_id: eventId, sort_order: finalSortOrder });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {initialData ? 'Edit Pivotal Step' : 'Add Pivotal Step'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="title">Step Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Complete certification course"
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe this step..."
            />
          </div>

          <div>
            <Label htmlFor="target_date">Target Date</Label>
            <Input
              id="target_date"
              type="date"
              value={formData.target_date || ''}
              onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
            />
          </div>

          {!initialData && existingSteps.length > 0 && (
            <div>
              <Label htmlFor="insert_position">Insert Position</Label>
              <Select value={insertPosition} onValueChange={setInsertPosition}>
                <SelectTrigger>
                  <SelectValue placeholder="Select position" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="end">Add at the end</SelectItem>
                  {existingSteps.map((step, index) => (
                    <SelectItem key={step.id || index} value={index.toString()}>
                      Insert after: {step.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex items-center space-x-2">
            <Checkbox
              id="is_completed"
              checked={formData.is_completed}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, is_completed: checked === true })
              }
            />
            <Label htmlFor="is_completed" className="cursor-pointer">
              Mark as completed
            </Label>
          </div>

          <div className="flex gap-2 justify-end pt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!formData.title}>
              {initialData ? 'Update' : 'Create'} Step
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
